/**
 * Public Governance Page — charstan.com/governance
 * Public-facing governance overview (no auth required)
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Shield, FileText, Globe, CheckCircle2, ArrowLeft, Lock,
  Scale, BookOpen, AlertTriangle, Building2, Eye, Layers
} from 'lucide-react';
import PublicLanguageSwitcher from '../components/PublicLanguageSwitcher';

const PublicGovernancePage = () => {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt-BR';

  const c = isPt ? {
    title: 'Governança',
    subtitle: 'Estrutura de governança, controles e compromissos de conformidade da CHARSTAN',
    sections: [
      { key: 'framework', icon: Layers, title: 'Estrutura de Governança', body: 'A CHARSTAN opera sob uma estrutura de governança em múltiplas camadas, projetada para conformidade regulatória, proteção de dados e integridade operacional em todos os mercados atendidos.', bullets: ['Conselho de governança com revisão trimestral', 'Matriz de separacao de funções aplicada em todos os modulos', 'Trilhas de auditoria imutaveis para todas as acoes do sistema', 'Avaliação e monitoramento continuo de riscos'] },
      { key: 'regulatory', icon: Scale, title: 'Alinhamento Regulatório', body: 'Nossa plataforma e projetada para alinhar-se com os principais frameworks regulatorios globais.', bullets: ['ISO 13485 — Sistemas de Gestão da Qualidade para Dispositivos Medicos', 'FDA 21 CFR Part 11 — Registros Eletronicos e Assinaturas', 'EU GMP Annex 11 — Sistemas Computadorizados', 'ISO 27001 — Gestão de Segurança da Informação', 'LGPD/GDPR — Proteção de Dados'] },
      { key: 'controls', icon: Lock, title: 'Controles Internos', body: 'Mantemos controles rigorosos em toda a plataforma para garantir a integridade dos dados e conformidade.', bullets: ['Controle de acesso baseado em funções (RBAC) com princípio de menor privilegio', 'Controle de versão de documentos com trilha de auditoria completa', 'Fluxos de aprovacao com assinaturas eletronicas', 'Retenção e arquivamento automatizado de dados', 'Backup e recuperacao de desastres'] },
      { key: 'transparency', icon: Eye, title: 'Transparencia', body: 'Estamos comprometidos com a transparencia em nossas operacoes, segurança e praticas de dados.', bullets: ['Relatorios regulares de conformidade disponiveis para clientes', 'Avaliações de segurança de terceiros', 'Status de conformidade publicado na Central de Confiança', 'Canal aberto para relatos de vulnerabilidades'] },
      { key: 'oversight', icon: Building2, title: 'Supervisão e Responsabilidade', body: 'Funções claras de supervisão garantem responsabilidade em todos os niveis.', bullets: ['Oficial de Proteção de Dados (DPO) designado', 'Comite de conformidade com reunioes mensais', 'Processo de escalacao de incidentes com SLAs definidos', 'Revisões de gestão anuais com documentacao completa'] },
    ],
    cta: 'Saiba mais na nossa Central de Confiança',
  } : {
    title: 'Governance',
    subtitle: 'CHARSTAN governance framework, controls, and compliance commitments',
    sections: [
      { key: 'framework', icon: Layers, title: 'Governance Framework', body: 'CHARSTAN operates under a multi-layered governance framework designed for regulatory compliance, data protection, and operational integrity across all markets served.', bullets: ['Governance board with quarterly review cycles', 'Separation of duties matrix enforced across all modules', 'Immutable audit trails for all system actions', 'Continuous risk assessment and monitoring'] },
      { key: 'regulatory', icon: Scale, title: 'Regulatory Alignment', body: 'Our platform is architected to align with key global regulatory frameworks.', bullets: ['ISO 13485 — Quality Management Systems for Medical Devices', 'FDA 21 CFR Part 11 — Electronic Records and Signatures', 'EU GMP Annex 11 — Computerised Systems', 'ISO 27001 — Information Security Management', 'LGPD/GDPR — Data Protection'] },
      { key: 'controls', icon: Lock, title: 'Internal Controls', body: 'We maintain rigorous controls across the platform to ensure data integrity and compliance.', bullets: ['Role-based access control (RBAC) with least-privilege principle', 'Document version control with complete audit trail', 'Approval workflows with electronic signatures', 'Automated data retention and archival', 'Backup and disaster recovery procedures'] },
      { key: 'transparency', icon: Eye, title: 'Transparency', body: 'We are committed to transparency in our operations, security, and data practices.', bullets: ['Regular compliance reports available to clients', 'Third-party security assessments', 'Compliance status published on Trust Center', 'Open channel for vulnerability disclosures'] },
      { key: 'oversight', icon: Building2, title: 'Oversight & Accountability', body: 'Clear oversight roles ensure accountability at every level.', bullets: ['Designated Data Protection Officer (DPO)', 'Compliance committee with monthly meetings', 'Incident escalation process with defined SLAs', 'Annual management reviews with full documentation'] },
    ],
    cta: 'Learn more at our Trust Center',
  };

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white" data-testid="governance-public-page">
      <header className="border-b border-[rgba(255,255,255,0.06)] bg-[#0A0E17]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#36A9E1] transition-colors" data-testid="governance-back-home">
            <ArrowLeft size={18} />
            <span className="font-semibold text-lg tracking-tight">CHARSTAN</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/trust" className="text-sm text-[#8B95A5] hover:text-white transition-colors">{isPt ? 'Confiança' : 'Trust'}</Link>
            <Link to="/standards" className="text-sm text-[#8B95A5] hover:text-white transition-colors">{isPt ? 'Normas' : 'Standards'}</Link>
            <Link to="/privacy" className="text-sm text-[#8B95A5] hover:text-white transition-colors">{isPt ? 'Privacidade' : 'Privacy'}</Link>
            <PublicLanguageSwitcher />
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-16 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-[rgba(54,169,225,0.1)]">
            <Shield className="text-[#36A9E1]" size={28} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="governance-title">{c.title}</h1>
        </div>
        <p className="text-[#A0AEC0] text-lg max-w-2xl">{c.subtitle}</p>
      </section>

      <main className="max-w-5xl mx-auto px-6 pb-12 space-y-6">
        {c.sections.map((section) => {
          const Icon = section.icon;
          return (
            <section key={section.key} className="bg-[#111827] border border-[rgba(255,255,255,0.06)] rounded-xl p-6 hover:border-[rgba(54,169,225,0.2)] transition-colors" data-testid={`governance-section-${section.key}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-lg bg-[rgba(54,169,225,0.1)] shrink-0">
                  <Icon className="text-[#36A9E1]" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                  <p className="text-[#A0AEC0] mt-2 leading-relaxed">{section.body}</p>
                </div>
              </div>
              <ul className="ml-12 space-y-2">
                {section.bullets.map((b, i) => (
                  <li key={i} className="text-[#8B95A5] text-sm flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-[#36A9E1] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <div className="text-center py-8">
          <Link to="/trust" className="inline-flex items-center gap-2 px-6 py-3 bg-[rgba(54,169,225,0.1)] border border-[rgba(54,169,225,0.3)] rounded-lg text-[#36A9E1] hover:bg-[rgba(54,169,225,0.2)] transition-colors font-medium" data-testid="governance-trust-link">
            <Shield size={18} />
            {c.cta}
          </Link>
        </div>
      </main>

      <footer className="border-t border-[rgba(255,255,255,0.06)] bg-[#0A0E17] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-[#4A5568] text-sm">&copy; 2026 CHARSTAN. {isPt ? 'Todos os direitos reservados.' : 'All rights reserved.'}</span>
          <div className="flex items-center gap-6">
            <Link to="/trust" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Confiança' : 'Trust'}</Link>
            <Link to="/privacy" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Privacidade' : 'Privacy'}</Link>
            <Link to="/help" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Ajuda' : 'Help'}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicGovernancePage;
